// modules/global/users/useUsers.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

import { IUser } from "../../../../interfaces/users";
import { handleUpload } from "../../../../utils/handleUpload";
import { createFile, createUser, listUsers, showUser, updateUser, deleteUser } from "@/services";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

type UseUsersArgs = {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  idParam?: string;
  /** evite passar `{}` inline; use `undefined` se não houver filtro */
  filter?: Record<string, unknown> | undefined;
};

type GetAllArgs = {
  page?: number;
  limit?: number;
  filter?: Record<string, unknown> | undefined;
};

const getInitialUser = (): IUser => ({
  id: undefined,
  name: "",
  email: "",
  password: "",
  active: false,
  idProfile: 0,
  profile: {} as any,
  idFilePhoto: null,
  photo: {} as any,
  type: undefined,
});

export const useUsers = ({
  disabledShowId,
  disabledGetAll,
  idParam,
  filter: filterInitial,
}: UseUsersArgs) => {
  const { id: idUser } = useParams();
  const navigate = useNavigate();

  const pathNew = "/global/users/new";
  const pathBack = "/global/users";

  const [users, setUsers] = useState<IUser[]>([]);
  const [user, setUser] = useState<IUser>(getInitialUser());
  const { filter, handleFilter, page, setPage, limit, setLimit } = useEntityQueryParams({
    filter: filterInitial,
    defaultLimit: 25,
  });
     // 1-based
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const resetUser = useCallback(() => setUser(getInitialUser()), []);

  const goEdit = useCallback((id: string | number) => { navigate(`/global/users/${id}`); }, [navigate]);
  const goBack = useCallback(() => { navigate(pathBack); }, [navigate]);
  const goAdd = useCallback(() => { navigate(pathNew); }, [navigate]);

  const handleUser = useCallback((key: keyof IUser, value: any) => {
    setUser((curr) => ({ ...curr, [key]: value }));
  }, []);

  const _getAll = useCallback(
    async ({ page = 1, limit, filter }: GetAllArgs) => {
      setLoading(true);
      try {
        const data = await listUsers({
          includes: [],          // leve para catálogos/autocomplete
          filter,                // pode ser undefined
          page,
          limit,
        });
        const list = data.users ?? [];
        setUsers(list);
        setPage((data as any).currentPage ?? (data as any).page ?? page);
        setLimit((data as any).limit ?? limit ?? 25);
        setTotalItems((data as any).totalItems ?? list.length ?? 0);
      } catch (e) {
        console.error(e);
        toast.error("Não foi possível carregar os usuários.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getAll = useMemo(
    () => debounce((args: GetAllArgs) => { void _getAll(args); }, 300),
    [_getAll]
  );

  const refresh = useCallback(
    () => { void _getAll({ page, limit, filter }); },
    [page, limit, filter, _getAll]
  );

  const get = useCallback(async ({ id }: { id: string | number }) => {
    setLoading(true);
    try {
      const { user: userShow } = await showUser({ id, includes: ["profile"] });
      setUser(userShow);
      return userShow;
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível carregar o usuário.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadImagem = useCallback(
    (fileUpload: any, onProgress?: (data: number) => void) => {
      setLoading(true);
      createFile({ file: fileUpload })
        .then(({ file: fileCreate }) =>
          handleUpload({
            dataUpload: { urlUpload: fileCreate.urlUpload, ...fileUpload },
            onProgress,
          }).then(async () => {
            await updateUser({ id: user.id!, user: { idFilePhoto: fileCreate.id } });
          })
        )
        .finally(() => setLoading(false));
    },
    [user.id]
  );

  const create = useCallback(() => {
    setLoading(true);
    createUser({ user })
      .then(({ user: userCreated }) => {
        toast.success("Usuário criado com sucesso!");
        goEdit(userCreated.id!);
      })
      .catch(() => toast.error("Falha ao criar usuário."))
      .finally(() => setLoading(false));
  }, [user, goEdit]);

  const update = useCallback(async () => {
    setLoading(true);
    try {
      await updateUser({ id: user.id!, user });
      toast.success("Usuário atualizado com sucesso!", { onClose: goBack, autoClose: 2000 });
    } catch {
      toast.error("Falha ao atualizar usuário.");
    } finally {
      setLoading(false);
    }
  }, [user, goBack]);

  const deleteUsers = useCallback(
    async (ids: Array<string | number>) => {
      if (!ids?.length) return;

      const uniqueIds = Array.from(new Set(ids.map(String)));
      const ac = new AbortController();

      const idToName = new Map<string, string>();
      (users || []).forEach((u) => {
        if (u?.id != null) idToName.set(String(u.id), u.name || `ID ${u.id}`);
      });

      setLoading(true);
      try {
        const results = await Promise.all(uniqueIds.map((uid) => deleteUser(uid, { signal: ac.signal })));

        const succeeded: string[] = [];
        const failed: { id: string; name: string }[] = [];

        for (let i = 0; i < results.length; i++) {
          const uid = uniqueIds[i];
          const { status, data } = results[i] as any;

          if (status >= 200 && status < 300) {
            succeeded.push(uid);
          } else {
            const name = idToName.get(uid) || data?.name || `ID ${uid}`;
            failed.push({ id: uid, name: String(name) });
          }
        }

        if (succeeded.length) {
          toast.success(
            succeeded.length > 1
              ? `${succeeded.length} usuários deletados com sucesso!`
              : `Usuário deletado com sucesso!`
          );
        }

        if (failed.length) {
          const names = failed.map((f) => f.name);
          toast.warn(
            names.length > 1
              ? `Não foi possível excluir ${names.length} usuários.`
              : `Não foi possível excluir o usuário.`
          );
        }

        await _getAll({ page, limit, filter });
      } catch (e) {
        console.error(e);
        toast.error("Falha ao excluir usuário(s).");
      } finally {
        setLoading(false);
        ac.abort();
      }
    },
    [users, _getAll, page, limit, filter]
  );

  // Busca auxiliar (autocomplete, etc.)
  const search = useEntitySearch({ list: listUsers, key: "users" });


  // efeitos
  useEffect(() => {
    if (!disabledGetAll) {
      getAll({ page, limit, filter });
    }
    return () => { getAll.cancel(); };
  }, [disabledGetAll, page, limit, filter, getAll]);

  useEffect(() => {
    const id = idParam || idUser;
    if (disabledShowId) return;

    if (id && id !== "new") {
      void get({ id });
    } else if (id === "new") {
      resetUser();
    }
  }, [idParam, idUser, disabledShowId, get, resetUser]);

  return {
    // estado
    users,
    user,
    totalItems,
    page,
    limit,
    loading,

    // setters
    setPage,
    setLimit,
    setTotalItems: (n: number) => setTotalItems(n),
    handleUser,
    handleFilter,

    // ações
    create,
    update,
    deleteUsers,

    // util
    search,
    uploadImagem,

    // navegação
    goEdit,
    goBack,
    goAdd,

    refresh,
    resetUser,
  };
};
